import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import ManageCategoryModal from '../../../../components/ManageCategoryModal/ManageCategoryModal';
import { writeBatch } from '../../../../libs/firestore-wrapper';
import { getCategoryDetails } from '../../../../modals/order';
import { ICategoryDetails } from '../../menu.types';
import { useMenuContext } from '../../../../context/MenuContext';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { LANG_TEXT } from '../../../../constants/language';

type SetToastState = React.Dispatch<React.SetStateAction<{ type: 'error' | 'success'; content: string }>>;

interface MenuCategoryManagerProps {
  open: boolean;
  handleClose: () => void;
  categories: ICategoryDetails[];
  setOpenToast: React.Dispatch<React.SetStateAction<boolean>>;
  setToast: SetToastState;
}

interface FormValues {
  [key: string]: string;
}

export const MenuCategoryManager: React.FC<MenuCategoryManagerProps> = ({ open, handleClose, categories, setOpenToast, setToast }) => {
  const { control, handleSubmit, getValues, reset } = useForm<FormValues>();
  const { t } = useTranslation();
  const { setFoodMenu } = useMenuContext();
  const { userInfo } = useAuthContext();

  const [editStates, setEditStates] = useState<{ [key: string]: { isEdit: boolean; isNew: boolean } }>({});
  const [id, setId] = useState<string>('');
  const [initialCategories, setInitialCategories] = useState<ICategoryDetails[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [warningTitle, setWarningTitle] = useState<string>('');
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenWarningModal = () => setOpenWarningModal(true);
  const handleCloseWarningModal = () => setOpenWarningModal(false);

  const removeUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
    const copy: Partial<T> = { ...obj };
    Object.keys(copy).forEach((k) => {
      if (copy[k] === undefined) delete copy[k];
    });
    return copy;
  };

  useEffect(() => {
    if (open) {
      setInitialCategories(categories);
      setEditStates({});
    }
  }, [open, categories]);

  const handleSwitchChange = (categoryId: string, isChecked: boolean) => {
    const switchCategories = initialCategories.map((category) => {
      if (category.id === categoryId) {
        return { ...category, enabled: isChecked, category_show: isChecked ? 1 : 0 };
      }
      return category;
    });

    setInitialCategories(switchCategories);
  };

  const handleEditClick = (category: any, status: boolean) => {
    const categoryId = category.id;
    if (status) {
      reset({ [`category_${categoryId}`]: category.title });
    }
    const editState = editStates[categoryId];
    if (editState?.isEdit === true && editState?.isNew === true) {
      const updatedCategories = initialCategories.filter((category) => category.id !== categoryId);

      setInitialCategories(updatedCategories);
      setEditStates((prevStates) => {
        const { [categoryId]: _, ...rest } = prevStates;
        return rest;
      });

      return;
    }

    setEditStates((prevStates) => ({
      ...prevStates,
      [categoryId]: {
        isEdit: status,
        isNew: false,
      },
    }));
  };

  const handleAddNewCategory = () => {
    const newCategoryId = uuidv4();
    const order = initialCategories.length;
    const newCategory: ICategoryDetails = {
      id: newCategoryId.toString(),
      title: '',
      order: order,
      foodIds: [],
      enabled: true,
      category_show: 1,
      restaurantId: userInfo.restaurant_id,
    };
    setInitialCategories([newCategory, ...initialCategories]);
    setEditStates((prevStates) => ({
      ...prevStates,
      [newCategoryId]: {
        isEdit: true,
        isNew: true,
      },
    }));
  };

  const handleDelete = () => {
    const categoryIndex = initialCategories.findIndex((category) => category.id === id);

    if (categoryIndex !== -1) {
      const newCategories = [...initialCategories.slice(0, categoryIndex), ...initialCategories.slice(categoryIndex + 1)];

      setInitialCategories(newCategories);
    }
    handleCloseModal();
  };

  const handleCheckIconClick = (categoryId: string) => {
    const newTitle = getValues(`category_${categoryId}`);
    const isDuplicate = initialCategories.some((category) => category.id !== categoryId && category.title.toLowerCase() === newTitle.toLowerCase());

    if (isDuplicate) {
      setWarningTitle(t(LANG_TEXT.WARNING_DUPLICATE_TITLE));
      handleOpenWarningModal();
      return;
    }

    const updatedCategories = initialCategories.map((category) => {
      if (category.id === categoryId) {
        return { ...category, title: newTitle };
      }
      return category;
    });

    setInitialCategories(updatedCategories);
    setEditStates((prevStates) => ({
      ...prevStates,
      [categoryId]: {
        isEdit: false,
        isNew: false,
      },
    }));
  };

  const onSubmit = handleSubmit(async () => {
    const validateTitle = initialCategories.some((category) => !category.title || !category.title.trim());
    const isEditCategogy = Object.values(editStates).some((value) => value.isEdit === true);

    if (validateTitle || isEditCategogy) {
      setWarningTitle(t(LANG_TEXT.CONTINUE_UPDATE_TEXT));
      handleOpenWarningModal();
      return;
    }

    const batch = writeBatch();
    const updatedIds = new Set(initialCategories.map((category) => category.id));
    const initialIds = new Set(categories.map((category) => category.id));
    const deletedIds = categories.filter((category) => !updatedIds.has(category.id)).map((category) => category.id);
    const addedIds = initialCategories.filter((category) => !initialIds.has(category.id)).map((category) => category.id);

    addedIds.forEach((id) => {
      const newCategory = initialCategories.find((category) => category.id === id);
      const foodDetailsRef = getCategoryDetails(id);
      if (newCategory) batch.set(foodDetailsRef, removeUndefined(newCategory));
    });

    deletedIds.forEach((id) => {
      const foodDetailsRef = getCategoryDetails(id);
      batch.delete(foodDetailsRef);
    });

    const reorderedCategories = initialCategories.map((category, index) => ({
      ...category,
      order: index,
    }));

    reorderedCategories.forEach((category) => {
      const foodDetailsRef = getCategoryDetails(category.id);
      batch.update(foodDetailsRef, removeUndefined({ ...category, title: category.title }));
    });

    try {
      setFoodMenu((prevStates) => ({
        ...prevStates,
        categories: initialCategories,
      }));
      await batch.commit();
      handleClose();
      setToast({
        content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE),
        type: 'success',
      });
      setOpenToast(true);
    } catch (error) {
      setToast({
        content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE),
        type: 'error',
      });
      setOpenToast(true);
    }
  });

  const moveCategory = (dragIndex: number, hoverIndex: number) => {
    const draggedCategory = initialCategories[dragIndex];
    const updatedCategories = [...initialCategories];
    updatedCategories.splice(dragIndex, 1);
    updatedCategories.splice(hoverIndex, 0, draggedCategory);
    setInitialCategories(updatedCategories);
  };

  const handleDeleteRequest = (categoryId: string) => {
    handleOpenModal();
    setId(categoryId);
  };

  return (
    <ManageCategoryModal
      open={open}
      handleClose={handleClose}
      categories={initialCategories}
      editStates={editStates}
      control={control}
      onSubmit={onSubmit}
      onSwitchChange={handleSwitchChange}
      onEditClick={handleEditClick}
      onCheckIconClick={handleCheckIconClick}
      onAddNewCategory={handleAddNewCategory}
      onMoveCategory={moveCategory}
      onDeleteRequest={handleDeleteRequest}
      onDeleteConfirm={handleDelete}
      deleteModalOpen={openModal}
      onDeleteModalClose={handleCloseModal}
      warningModalOpen={openWarningModal}
      warningTitle={warningTitle}
      onWarningModalClose={handleCloseWarningModal}
    />
  );
};

export default MenuCategoryManager;
